import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { AgentDownloads } from '../types/models/Agent';
import type { ApiResponseAgentDownloads } from '../types/api/ApiResponseAgent';

interface UseAgentDownloadsReturn {
  downloads: AgentDownloads | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * GET /organization/agent/downloads — installer metadata (version, filename, sha256, size) for
 * the agent binaries this backend deployment can serve. Auth-only, not tenant-scoped.
 *
 * Failures deliberately do NOT surface as a page error: the download routes ship with the next
 * backend tag, so until then the live API answers 404/route-not-found (deployment lag), and a
 * metadata-less deployment legitimately answers `available: false`. Consumers must treat
 * `downloads === null` exactly like `available: false` — degrade the download button, keep the
 * instructions visible. `error` is exposed for parity with `useAgentFeature` but is informational
 * only.
 */
export const useAgentDownloads = (): UseAgentDownloadsReturn => {
  const [downloads, setDownloads] = useState<AgentDownloads | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDownloads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/organization/agent/downloads');
      const body = (await response.json().catch(() => null)) as ApiResponseAgentDownloads | null;

      if (!response.ok || body?.status !== 'success' || !body.data?.downloads) {
        setDownloads(null);
        setError(body?.message ?? 'Failed to load the agent download metadata');
        return;
      }

      setDownloads(body.data.downloads);
      setError(null);
    } catch (err) {
      setDownloads(null);
      setError(err instanceof Error ? err.message : 'An error occurred while loading the agent download metadata');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchDownloads();
    }
  }, [auth.isAuthenticated, fetchDownloads]);

  return { downloads, loading, error, refetch: fetchDownloads };
};
